import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";
import { TrendingUp } from "lucide-react";

const fmtDay = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
const DAY_LABELS = ["Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam.", "Dim."];
const DONUT_COLORS = ["#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#F43F5E", "#64748B", "#0EA5E9"];

const Card = ({ title, subtitle, action, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={testid}>
    <div className="flex items-start justify-between gap-2 mb-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

export function EvolutionChart({ series }) {
  const empty = !series || series.every((s) => !s.en_ligne && !s.fixes && !s.passes);
  return (
    <Card title="Nombre de rendez-vous" subtitle="Évolution sur la période sélectionnée" testid="rdv-chart-evolution">
      {empty ? (
        <p className="text-sm text-slate-400 py-14 text-center" data-testid="rdv-chart-empty">Aucun rendez-vous sur cette période.</p>
      ) : (
        <div className="h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
              <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94A3B8" }}
                interval={Math.max(0, Math.floor(series.length / 8))} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v, name) => [v, { en_ligne: "RDV en ligne", fixes: "RDV fixés", passes: "RDV passés" }[name] || name]}
                labelFormatter={fmtDay} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Area type="monotone" dataKey="en_ligne" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.08} strokeWidth={2} name="en_ligne" />
              <Area type="monotone" dataKey="fixes" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.08} strokeWidth={2} name="fixes" />
              <Area type="monotone" dataKey="passes" stroke="#10B981" fill="#10B981" fillOpacity={0.08} strokeWidth={2} name="passes" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
        {[["#8B5CF6", "RDV en ligne"], ["#3B82F6", "RDV fixés"], ["#10B981", "RDV passés"]].map(([c, l]) => (
          <span key={l} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />{l}</span>
        ))}
      </div>
    </Card>
  );
}

export function SourcesDonut({ sources }) {
  const total = (sources || []).reduce((s, x) => s + x.count, 0);
  return (
    <Card title="Sources des rendez-vous" subtitle="D'où proviennent vos RDV" testid="rdv-chart-sources">
      {total === 0 ? (
        <p className="text-sm text-slate-400 py-14 text-center" data-testid="rdv-sources-empty">Aucune donnée sur cette période.</p>
      ) : (
        <>
          <div className="h-[170px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sources} dataKey="count" nameKey="label" innerRadius={48} outerRadius={72}
                  strokeWidth={2} stroke="#fff" paddingAngle={2}>
                  {sources.map((s, i) => <Cell key={s.source} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, name) => [v, name]} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="space-y-1.5 mt-2">
            {sources.map((s, i) => (
              <li key={s.source} className="flex items-center gap-2 text-xs" data-testid={`rdv-source-${s.source}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                <span className="flex-1 text-slate-600 truncate">{s.label}</span>
                <span className="font-semibold text-slate-900">{s.count}</span>
                <span className="text-slate-400 w-9 text-right">{s.pct} %</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

export function TransformationWidget({ transformation }) {
  const t = transformation || { devis: 0, client: 0, projet: 0, sans_suite: 0 };
  const rows = [
    { key: "devis", label: "RDV → Devis", value: t.devis, color: "#8B5CF6" },
    { key: "client", label: "RDV → Client", value: t.client, color: "#3B82F6" },
    { key: "projet", label: "RDV → Projet", value: t.projet, color: "#10B981" },
    { key: "sans_suite", label: "Sans suite", value: t.sans_suite, color: "#94A3B8" },
  ];
  return (
    <Card title="Taux de transformation" subtitle="RDV passés convertis en opportunités" testid="rdv-transformation">
      <ul className="space-y-3.5">
        {rows.map((r) => (
          <li key={r.key} data-testid={`rdv-transfo-${r.key}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-600 font-medium">{r.label}</span>
              <span className="font-bold text-slate-900">{r.value} %</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${r.value}%`, background: r.color }} />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function AvailabilityWidget({ avail }) {
  const days = avail?.days || [];
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const dayLabel = (d) => d.date === today ? "Aujourd'hui" : d.date === tomorrow ? "Demain"
    : `${DAY_LABELS[d.weekday]} ${fmtDay(d.date)}`;
  return (
    <Card title="Prochaines disponibilités" subtitle="Selon vos règles et votre agenda"
      testid="rdv-availability"
      action={<Link to="/agenda" data-testid="rdv-availability-agenda" className="text-xs font-semibold text-[#0369A1] hover:underline shrink-0">Voir l'agenda ›</Link>}>
      {days.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center" data-testid="rdv-availability-empty">Aucun créneau libre dans les prochains jours.</p>
      ) : (
        <div className="space-y-3">
          {days.map((d) => (
            <div key={d.date}>
              <p className="text-xs font-semibold text-slate-500 capitalize mb-1.5">{dayLabel(d)}</p>
              <div className="flex flex-wrap gap-1.5">
                {d.slots.slice(0, 4).map((s) => (
                  <span key={s.start} data-testid={`rdv-slot-${d.date}-${s.start}`}
                    className="text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg px-2 py-1">
                    {s.start} — Disponible
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function ConversionIcon() { return <TrendingUp className="w-4 h-4" />; }
